import { SLIGHTLY_OVERUTILIZED_THRESHOLD } from '@constants'
import { memberCheckTypeToReadableMap } from '@domainConstants'
import {
  buildSupportsByElementIndex,
  getSupportsOfElement,
  type SupportsByElementIndex,
} from '@domainUtils'
import { cloneDeep, find, reject, round, toNumber } from 'lodash-es'
import type { Theme } from '@mui/material/styles'
import { defaultSteelCSShape } from '@editor/components/SingleElementCSForm/schema'
import type { HighlightedCheck } from '@editorStores'
import type { CharacteristicSupportReactionRow } from '@queries'
import { getSlabBeamCrossSectionFromAssembly } from 'src/utils/assemblies'
import { isVibrationError } from '../../utils'
import {
  HOT_DIMENSIONING_MEMBER_TYPES,
  SHEAR_CHECK_TYPES,
  STEEL_FUSSPUNKT_MEMBER_TYPES,
} from './constants'
import type { DesignFormValues, MemberCheckRow } from './types'

export { isVibrationError }

export const cloneLineLikeCrossSection = (
  cs: CrossSectionTimberOrCLTOrSteel,
): CrossSectionTimberOrCLTOrSteel => cloneDeep(cs)

export const minimalSteelCrossSectionFromDims = (
  widthM: number,
  heightM: number,
): SteelCrossSection => ({
  kind: 'Steel',
  shape: {
    kind_literal: 'SteelCS',
    profile: 'IPE 200',
    width: widthM || 0.1,
    height: heightM || 0.2,
  },
  material: {
    kind: 'Steel',
    identifier: 'S235',
    unique_name: 'S235',
  },
  usage_class: 'One',
})

export const timberCrossSectionFromDims = (
  widthM: number,
  heightM: number,
  identifier = 'C24',
  usageClass: UsageClass = 'One',
): TimberCrossSection => ({
  kind: 'Timber',
  shape: {
    kind_literal: 'RectangularCS',
    width: widthM,
    height: heightM,
  },
  material: {
    kind: 'Softwood',
    kind_literal: 'Softwood',
    identifier,
  } as unknown as TimberMaterial,
  usage_class: usageClass,
})

/** Default rectangular timber cross section (6 cm × 24 cm) when switching material category. */
export const DEFAULT_TIMBER_CROSS_SECTION_DIMS_M = {
  width: 0.1,
  height: 0.1,
} as const

/** Backend identifiers to preselect when switching material category in the detail pane. */
const DEFAULT_MATERIAL_IDENTIFIER_BY_KIND: Partial<Record<MaterialKind, string>> = {
  Softwood: 'SW24',
  Glulam: 'GL24c',
  Steel: 'S235',
}

export const defaultMaterialForMaterialKind = (
  materials: Material[],
  kind: MaterialKind,
): Material | undefined => {
  const preferredId = DEFAULT_MATERIAL_IDENTIFIER_BY_KIND[kind]
  if (preferredId) {
    const match = find(materials, m => m.identifier.toLowerCase() === preferredId.toLowerCase())
    if (match) return match
  }
  return materials[0]
}

/** Builds a complete cross section after switching timber/steel product category. */
export const crossSectionForMaterialCategory = (
  prev: CrossSection,
  nextMaterialKind: MaterialKind,
  firstMaterial: Material,
  firstSteelShape: SteelCSShape | undefined,
): CrossSection => {
  const usageFromPrev: UsageClass =
    prev.kind === 'Timber' ? prev.usage_class : prev.usage_class ?? 'One'

  if (nextMaterialKind === 'Steel') {
    const shape = firstSteelShape ?? defaultSteelCSShape
    const steelMat = firstMaterial as SteelMaterial
    return {
      kind: 'Steel',
      material: steelMat,
      shape,
    }
  }

  const timberDims = prev.kind === 'Timber' ? prev.shape : DEFAULT_TIMBER_CROSS_SECTION_DIMS_M

  return {
    kind: 'Timber',
    material: firstMaterial as TimberMaterial,
    shape: {
      kind_literal: 'RectangularCS',
      width: timberDims.width,
      height: timberDims.height,
    },
    usage_class: usageFromPrev,
  }
}

/** CS for Bemessungs-Form: Zuordnung; sonst Plattenbalken-/Dachplattenbalken-Querschnitt aus der zugewiesenen Baugruppe (vollständiges Material). */
export function resolveResults2DetailPaneCrossSection({
  elementId,
  elementType,
  elementCrossSectionAssignment,
  slabBeamGuidToAssembly,
}: {
  elementId?: string
  elementType?: ElementTypes
  elementCrossSectionAssignment: ElementCSAssignment[]
  slabBeamGuidToAssembly: Record<string, Assembly>
}): CrossSectionTimberOrCLTOrSteel | undefined {
  if (!elementId) return undefined
  const fromAssignment = find(elementCrossSectionAssignment, { element_guid: elementId })
    ?.element_cs as CrossSectionTimberOrCLTOrSteel | undefined
  if (fromAssignment) return fromAssignment

  if (elementType === 'slab_beams' || elementType === 'roof_slab_beams') {
    const assembly = slabBeamGuidToAssembly[elementId]
    if (assembly) {
      try {
        return getSlabBeamCrossSectionFromAssembly(assembly)
      } catch {
        return undefined
      }
    }
  }
  return undefined
}

/**
 * Backend `calc-element-checks` DTO discriminators erwarten bei Holzmaterial `kind_literal`
 * zusätzlich zu `kind`.
 */
export function hydrateCrossSectionForCalcElementChecksRequest(
  cs: CrossSectionTimberOrCLTOrSteel,
): CrossSectionTimberOrCLTOrSteel {
  const cloned = cloneDeep(cs)
  if (cloned.kind !== 'Timber') return cloned
  const m = cloned.material as TimberMaterial & { kind_literal?: string }
  const kindLiteral: 'Softwood' | 'Glulam' | 'LVL' | undefined =
    m.kind === 'Softwood'
      ? 'Softwood'
      : m.kind === 'Glulam'
      ? 'Glulam'
      : m.kind === 'LVL'
      ? 'LVL'
      : undefined

  if (kindLiteral !== undefined && m.kind_literal !== kindLiteral) {
    Object.assign(cloned.material as object, { kind_literal: kindLiteral })
  }

  return cloned
}

/** Same beam-like filter as `CSAndAPPopupV2` → `postCalcPositionChecks`. */
const nonBeamLikeElementTypesForPositionChecks: ElementTypes[] = [
  'foundation',
  'inner_walls',
  'outer_walls',
  'slabs',
  'roof_slabs',
  'vertical_roof_slabs',
  'vertical_slabs',
]

export function beamLikeGuidsForCalcPositionChecks({
  elementId,
  verticalTransmissionGraph,
  elementTypeLookup,
}: {
  elementId: string
  verticalTransmissionGraph: VerticalTransmissionGraph | null | undefined
  elementTypeLookup: Record<string, ElementTypes | undefined>
}): string[] {
  const supports = verticalTransmissionGraph
    ? getSupportsOfElement(elementId, verticalTransmissionGraph)
    : []
  const guids = [
    elementId,
    ...supports.map(s => s.guid).filter((g): g is string => typeof g === 'string' && g.length > 0),
  ]
  return reject(guids, guid =>
    nonBeamLikeElementTypesForPositionChecks.includes(elementTypeLookup[guid] as ElementTypes),
  )
}

/** Kommentar Positionsliste + Vorbemerkungen für ein Bauteil innerhalb einer Positionsliste. */
export const commentTextsForPositionGroupingBundle = (
  bundles: MemberPositionBundle[] | null | undefined,
  guid: string,
): { comment: string; extendedComment: string; notes: string } => {
  if (!guid || !bundles?.length) {
    return { comment: '', extendedComment: '', notes: '' }
  }
  const bundle = bundles.find(
    b => b.representative_position === guid || (b.representative_for ?? []).includes(guid),
  )
  return {
    comment: bundle?.comment ?? '',
    extendedComment: bundle?.extended_comment ?? '',
    notes: bundle?.notes ?? '',
  }
}

/** `exported` nur für die Repräsentantenposition im Bundle; Kinder in `representative_for` bleiben false (wie in der Liste). */
export const exportedFlagForPositionGroupingRepresentativeOnly = (
  bundles: MemberPositionBundle[] | null | undefined,
  guid: string,
): boolean => {
  if (!guid || !bundles?.length) return false
  const bundle = bundles.find(
    b => b.representative_position === guid || (b.representative_for ?? []).includes(guid),
  )
  if (!bundle) return false
  return bundle.representative_position === guid ? Boolean(bundle.exported) : false
}

/**
 * `exported` to persist on the bundle when saving from the detail pane.
 * Non-representative rows use `exported: false` in the form — keep the stored bundle flag instead.
 */
export const resolveExportedForPositionGroupingSave = (
  elementId: string | undefined,
  grouping: MemberPositionBundle[] | undefined,
  formExported: boolean | undefined,
): boolean => {
  if (!elementId || !grouping?.length) return Boolean(formExported)
  const bundle = grouping.find(
    b =>
      b.representative_position === elementId || (b.representative_for ?? []).includes(elementId),
  )
  if (!bundle) return Boolean(formExported)
  return bundle.representative_position === elementId
    ? Boolean(formExported)
    : Boolean(bundle.exported)
}

export const buildDefaultDesignFormValues = ({
  elementGuid,
  memberCheckSettingsSelectedElement,
  exported,
  crossSectionFromAssignment,
  comment,
  extendedComment,
  notes,
  showInPositionPlan,
  checksSelectedElement,
  supportConfigsForChecks,
  supportsOfElement,
}: {
  elementGuid: string
  memberCheckSettingsSelectedElement: SettingsOnMember | null
  exported?: boolean
  crossSectionFromAssignment?: CrossSectionTimberOrCLTOrSteel
  comment?: string
  extendedComment?: string
  notes?: string
  showInPositionPlan?: boolean
  checksSelectedElement: CombinedPositionCheck[]
  supportConfigsForChecks: SupportConfigForChecks[]
  supportsOfElement: TargetGuidAndRelativePosition[]
}): DesignFormValues => {
  const crossSection: CrossSectionTimberOrCLTOrSteel | null = crossSectionFromAssignment
    ? cloneLineLikeCrossSection(crossSectionFromAssignment)
    : null

  return {
    elementGuid,
    memberCheckSettingsSelectedElement,
    exported: exported ?? false,
    comments: {
      positionList: comment ?? '',
      preliminaryRemarks: extendedComment ?? '',
      notes: notes ?? '',
      showInPositionPlan: showInPositionPlan ?? false,
    },
    crossSection,
    checksSelectedElement,
    supportConfigsForChecks,
    supportsOfElement,
  }
}

const numberFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export const fmt = (value: number) => numberFormatter.format(value)

// Matches MemberChecksTable's formatUtilization: render 0 as empty.
export const fmtUtilization = (value: number) => (value === 0 ? '' : fmt(value))

const allowedOverutilizationFraction = (allowedOverutilizationPercent = 0): number => {
  if (!Number.isFinite(allowedOverutilizationPercent)) return 0
  return Math.max(0, allowedOverutilizationPercent) / 100
}

const roundDisplayedUtilization = (value: number): number =>
  Math.round((value + Number.EPSILON) * 100) / 100

/** η above the allowed limit up to the red threshold — slightly over-utilized (yellow / brown). */
export const isUtilizationWarning = (value: number, allowedOverutilizationPercent = 0): boolean => {
  const displayedValue = roundDisplayedUtilization(value)
  const allowance = allowedOverutilizationFraction(allowedOverutilizationPercent)
  return (
    displayedValue > 1 + allowance && displayedValue <= SLIGHTLY_OVERUTILIZED_THRESHOLD + allowance
  )
}

/** η above allowed limit + 5% — over-utilized (red). */
export const isUtilizationError = (value: number, allowedOverutilizationPercent = 0): boolean => {
  const displayedValue = roundDisplayedUtilization(value)
  const allowance = allowedOverutilizationFraction(allowedOverutilizationPercent)
  return displayedValue > SLIGHTLY_OVERUTILIZED_THRESHOLD + allowance
}

/** η cell background — ok below allowed limit, yellow for next 5%, red after that. */
export const utilizationCellSx = (
  value: number,
  theme: Theme,
  allowedOverutilizationPercent = 0,
): { background: string; color: string; fontWeight: number } | null => {
  if (isUtilizationError(value, allowedOverutilizationPercent)) {
    return {
      background: theme.results.utilization.overutilized.light,
      color: theme.palette.text.primary,
      fontWeight: 400,
    }
  }
  if (isUtilizationWarning(value, allowedOverutilizationPercent)) {
    return {
      background: theme.results.utilization.slightlyOverutilized.light,
      color: theme.palette.text.primary,
      fontWeight: 400,
    }
  }
  return null
}

/** Matches `TimberMemberChecksTable` `formatPosition` (de-DE, 2 decimals). */
export const formatMemberCheckPosition = (position: number) => fmt(position)

export const formatKn = (newton: number): string => fmt(newton / 1000)

const cmNumberFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

/** Placeholder / read-only cm label for meters stored in the form (matches `@ui/forms` CentimeterField). */
export const formatCmFromMeters = (meters: number): string => {
  const cm = Math.round(meters * 10_000) / 100
  return cmNumberFormatter.format(cm)
}

export const getMemberCheckSpecificSettingsKey = (
  check: CombinedPositionCheck,
): Pick<MemberCheckSpecificSetting, 'check_type' | 'relative_position'> => ({
  check_type: check.check_type,
  relative_position: 'relative_position' in check ? String(check.relative_position) : null,
})

export const memberCheckSpecificSettingsMatch = (
  setting: MemberCheckSpecificSetting,
  key: Pick<MemberCheckSpecificSetting, 'check_type' | 'relative_position'>,
): boolean =>
  setting.check_type === key.check_type &&
  (setting.relative_position == null || key.relative_position == null
    ? (setting.relative_position ?? null) === key.relative_position
    : matchesRelativePosition(setting.relative_position, key.relative_position))

/** Check types whose tolerances are bound to a specific support position. */
const SUPPORT_COMPRESSION_CHECK_TYPES = new Set<MemberCheckType>([
  'SupportCompression',
  'SteelSupportCompression',
])

export type MemberCheckSettingSupportContext = {
  relativePosition: string
  /** Element that supports the member at this position; null e.g. for foundations. */
  supportingElementGuid: string | null
}

export type MemberCheckSettingValidationContext = {
  /** Current cross section of the element; null/undefined → unknown, comparison is skipped. */
  crossSection?: CrossSectionTimberOrCLTOrSteel | null
  /** Current supports of the element; undefined → unknown, comparison is skipped. */
  supports?: MemberCheckSettingSupportContext[]
}

/**
 * Pre-indexed support layout for O(1) per-element lookups. Build once per graph
 * snapshot and reuse across rows/components that share the same graph. Shares
 * the single graph traversal in {@link buildSupportsByElementIndex}.
 */
export type MemberCheckSettingSupportsContextIndex = SupportsByElementIndex

export const buildMemberCheckSettingSupportsContextIndex = (
  graph: VerticalTransmissionGraph | undefined | null,
): MemberCheckSettingSupportsContextIndex | undefined => {
  if (!graph) return undefined
  return buildSupportsByElementIndex(graph)
}

export const getMemberCheckSettingSupportsContextFromIndex = (
  elementGuid: string | undefined,
  index: MemberCheckSettingSupportsContextIndex | undefined,
): MemberCheckSettingSupportContext[] | undefined => {
  if (!elementGuid || !index) return undefined
  // Known graph but no supports for this element ⇒ empty layout, not "unknown".
  // Callers distinguish [] (known empty) from undefined (context not loaded).
  // Foundations (null supporting element) are kept; supports without a relative
  // position carry no tolerance meaning and are dropped.
  return (index.get(elementGuid) ?? [])
    .filter(support => support.relativePosition != null)
    .map(support => ({
      relativePosition: support.relativePosition as string,
      supportingElementGuid: support.supportingElementGuid,
    }))
}

/**
 * All supports of an element (including ones without a target element, e.g.
 * foundations) with the guid of the supporting element where one exists.
 */
export const buildMemberCheckSettingSupportsContext = (
  elementGuid: string | undefined,
  graph: VerticalTransmissionGraph | undefined | null,
  index?: MemberCheckSettingSupportsContextIndex,
): MemberCheckSettingSupportContext[] | undefined => {
  if (!elementGuid || !graph) return undefined
  const resolvedIndex = index ?? buildMemberCheckSettingSupportsContextIndex(graph)
  return getMemberCheckSettingSupportsContextFromIndex(elementGuid, resolvedIndex)
}

/**
 * Identity of a cross section for reference-data comparison. Only stable
 * identifying fields (kind, dimensions/profile, material identifier) are
 * compared so a backend round-trip (added defaults, key order) cannot break a
 * match for an unchanged cross section.
 */
const crossSectionReferenceFingerprint = (
  cs: CrossSectionTimberOrCLTOrSteel | null | undefined,
): string | null => {
  if (!cs) return null
  if (cs.kind === 'CLT') {
    return JSON.stringify({
      kind: cs.kind,
      identifier: cs.identifier ?? cs.unique_name ?? null,
      width: cs.width ?? null,
    })
  }
  const material = cs.material as { identifier?: string; unique_name?: string } | undefined
  return JSON.stringify({
    kind: cs.kind,
    profile: 'profile' in cs.shape ? cs.shape.profile ?? null : null,
    height: cs.shape.height ?? null,
    width: cs.shape.width ?? null,
    material: material?.identifier ?? material?.unique_name ?? null,
  })
}

const isSupportSpecificMemberCheckSetting = (
  setting: Pick<MemberCheckSpecificSetting, 'check_type' | 'relative_position'>,
): boolean =>
  SUPPORT_COMPRESSION_CHECK_TYPES.has(setting.check_type) && setting.relative_position != null

/**
 * Reference data describing the design context a tolerance is entered/saved
 * against. Attached as soon as the user enters a value and refreshed on save.
 */
export const buildMemberCheckSettingReferenceData = (
  setting: Pick<MemberCheckSpecificSetting, 'check_type' | 'relative_position'>,
  context: MemberCheckSettingValidationContext,
  supportConfigs: SupportConfig[] = [],
): MemberCheckSettingReferenceData => {
  const referenceData: MemberCheckSettingReferenceData = {
    cross_section: context.crossSection ?? null,
  }
  if (isSupportSpecificMemberCheckSetting(setting) && context.supports) {
    const currentSupport = context.supports.find(support =>
      matchesRelativePosition(support.relativePosition, setting.relative_position ?? undefined),
    )
    referenceData.support_relative_positions = context.supports.map(
      support => support.relativePosition,
    )
    referenceData.supporting_element_guid = currentSupport?.supportingElementGuid ?? null
    referenceData.support_config =
      supportConfigs.find(config =>
        matchesRelativePosition(config.relative_position, setting.relative_position ?? undefined),
      ) ?? null
  }
  return referenceData
}

/**
 * A tolerance only applies while the design context it was entered/saved
 * against still matches: same cross section and — for support compression
 * checks — same support layout and supporting element. A tolerance without
 * reference data never applies (it stems from a context we know nothing
 * about, e.g. saved through an outdated backend). Context parts that are
 * unknown (not loaded / not captured) are skipped rather than treated as a
 * mismatch.
 */
export const isMemberCheckSpecificSettingApplicable = (
  setting: MemberCheckSpecificSetting,
  context?: MemberCheckSettingValidationContext,
): boolean => {
  const reference = setting.reference_data
  if (!reference) return false
  if (!context) return true

  if (
    reference.cross_section != null &&
    context.crossSection != null &&
    crossSectionReferenceFingerprint(reference.cross_section) !==
      crossSectionReferenceFingerprint(context.crossSection)
  ) {
    return false
  }

  if (!isSupportSpecificMemberCheckSetting(setting)) return true
  // null support_relative_positions ⇔ support context was unknown when saving,
  // so the remaining reference fields carry no meaning either.
  if (!context.supports || reference.support_relative_positions == null) return true

  const referencePositions = [...reference.support_relative_positions]
    .map(toNumber)
    .sort((a, b) => a - b)
  const currentPositions = context.supports
    .map(support => toNumber(support.relativePosition))
    .sort((a, b) => a - b)
  if (referencePositions.length !== currentPositions.length) return false
  if (
    referencePositions.some(
      (position, i) => !matchesRelativePosition(position, currentPositions[i]),
    )
  ) {
    return false
  }

  const currentSupport = context.supports.find(support =>
    matchesRelativePosition(support.relativePosition, setting.relative_position ?? undefined),
  )
  if (!currentSupport) return false

  return (reference.supporting_element_guid ?? null) === currentSupport.supportingElementGuid
}

/**
 * Prepares check-specific settings for saving: drops settings that are stale
 * for the current design context (the user already sees them as unset) and
 * stamps the remaining ones with reference data describing that context, so
 * later reads can detect when cross section or support situation changed.
 */
export const withMemberCheckSettingsReferenceData = <T extends SettingsOnMember>(
  settings: T,
  context: MemberCheckSettingValidationContext,
): T => {
  const specificSettings = settings.check_specific_settings ?? []
  if (specificSettings.length === 0) return settings

  const supportConfigs = isTimberLikeSettings(settings) ? settings.support_configs : []
  const checkSpecificSettings = specificSettings
    .filter(setting => isMemberCheckSpecificSettingApplicable(setting, context))
    .map(
      (setting): MemberCheckSpecificSetting => ({
        ...setting,
        reference_data: buildMemberCheckSettingReferenceData(setting, context, supportConfigs),
      }),
    )

  return { ...settings, check_specific_settings: checkSpecificSettings }
}

export const getMemberCheckSpecificSettingForCheck = (
  check: CombinedPositionCheck,
  checkSpecificSettings: MemberCheckSpecificSetting[] = [],
  context?: MemberCheckSettingValidationContext,
): MemberCheckSpecificSetting | undefined => {
  const key = getMemberCheckSpecificSettingsKey(check)
  return checkSpecificSettings.find(
    setting =>
      memberCheckSpecificSettingsMatch(setting, key) &&
      isMemberCheckSpecificSettingApplicable(setting, context),
  )
}

export const getAllowedOverutilizationPercentForCheck = (
  check: CombinedPositionCheck | undefined,
  checkSpecificSettings: MemberCheckSpecificSetting[] = [],
  context?: MemberCheckSettingValidationContext,
): number => {
  if (!check) return 0
  const value =
    getMemberCheckSpecificSettingForCheck(check, checkSpecificSettings, context)?.value ?? 0
  return Number.isFinite(value) ? Math.max(0, value) : 0
}

// Mirrors the row builder in TimberMemberChecksTable so the data shown here
// stays in sync with what Results / MemberChecksTable already display.
export const buildMemberCheckRows = (
  checks: CombinedPositionCheck[],
  checkSpecificSettings: MemberCheckSpecificSetting[] = [],
  context?: MemberCheckSettingValidationContext,
): MemberCheckRow[] => {
  return [...checks]
    .map((check, i): MemberCheckRow => {
      const maxUtilization = 'max_utilization' in check ? check.max_utilization : 0
      const maxUtilizationC90 =
        'max_utilization' in check && check.check_type === 'SupportCompression'
          ? check.max_utilization
          : 0
      const position = 'relative_position' in check ? toNumber(check.relative_position) : 0
      const id = `${'relative_position' in check ? check.relative_position : 'vibration'}-${i}`
      return {
        id,
        label: memberCheckTypeToReadableMap[check.check_type] ?? check.check_type,
        maxUtilization,
        maxUtilizationC90,
        position,
        checkType: check.check_type,
        check,
        checkSpecificSetting: getMemberCheckSpecificSettingForCheck(
          check,
          checkSpecificSettings,
          context,
        ),
      }
    })
    .sort((a, b) => b.maxUtilization - a.maxUtilization)
}

export const matchesRelativePosition = (a?: string | number, b?: string | number): boolean =>
  Math.abs(toNumber(a) - toNumber(b)) < 0.001

/**
 * Mirrors the SupportsGridRow filtering: for rips and columns the support that
 * does not map to a known supporting element (elementIndex < 0) is the "second
 * support" and must be hidden. Returns a predicate that, given a support's
 * relative position, tells whether that support should be hidden — so the
 * "Auflager" and "Charakteristische Auflagerkräfte" tables stay in sync with
 * the supports grid.
 */
export const buildHiddenSupportPositionPredicate = (
  rootElementType: ElementTypes | undefined,
  supportConfigs: SupportConfig[] | null | undefined,
  supportConfigIndexToElementIndex: Record<number, number>,
): ((relativePosition?: string | number) => boolean) => {
  if (rootElementType !== 'rips' && rootElementType !== 'columns') return () => false

  return relativePosition => {
    const index = (supportConfigs ?? []).findIndex(config =>
      matchesRelativePosition(config.relative_position, relativePosition),
    )
    return index >= 0 && (supportConfigIndexToElementIndex[index] ?? -1) < 0
  }
}

export const SUPPORT_COMPRESSION_MEMBER_CHECK_TYPES = new Set<MemberCheckType>([
  'SupportCompression',
  'SteelSupportCompression',
])

export const isSupportCompressionMemberCheckType = (checkType: MemberCheckType): boolean =>
  SUPPORT_COMPRESSION_MEMBER_CHECK_TYPES.has(checkType)

/**
 * Whether a check belongs to the currently open settings tab. Drives the
 * relevance dot next to the check name (the row background highlight is reserved
 * for the explicit selection).
 */
export const isCheckRelevantForSettingsTab = (
  checkType: MemberCheckType,
  settingsTabKey: string | null,
): boolean => {
  if (!settingsTabKey) return false
  switch (settingsTabKey) {
    case 'ap':
      return isSupportCompressionMemberCheckType(checkType)
    case 'shearCheck':
      return SHEAR_CHECK_TYPES.has(checkType)
    case 'hotDimensioning':
      return HOT_DIMENSIONING_MEMBER_TYPES.has(checkType)
    case 'vibration':
      return checkType === 'Vibration'
    case 'steelConfig':
      return STEEL_FUSSPUNKT_MEMBER_TYPES.has(checkType)
    default:
      return false
  }
}

/** A support is keyed by position only; one support exists per position. */
export const isSelectedSupportHighlightMatch = (
  elementGuid: string,
  highlightedCheck: HighlightedCheck,
  relativePosition: string | number | undefined,
): boolean =>
  highlightedCheck != null &&
  highlightedCheck.kind === 'support' &&
  highlightedCheck.elementGuid === elementGuid &&
  matchesRelativePosition(highlightedCheck.relativePosition, relativePosition)

export const toggleSelectedSupportHighlight = (
  elementGuid: string,
  relativePosition: string | number,
  highlightedCheck: HighlightedCheck,
  setHighlightedCheck: (value: HighlightedCheck) => void,
): void => {
  if (isSelectedSupportHighlightMatch(elementGuid, highlightedCheck, relativePosition)) {
    setHighlightedCheck(null)
    return
  }
  setHighlightedCheck({
    kind: 'support',
    elementGuid,
    relativePosition: String(relativePosition),
  })
}

/** Select a support highlight, keeping it set even when it is already the selected one. */
export const setSelectedSupportHighlight = (
  elementGuid: string,
  relativePosition: string | number,
  setHighlightedCheck: (value: HighlightedCheck) => void,
): void => {
  setHighlightedCheck({
    kind: 'support',
    elementGuid,
    relativePosition: String(relativePosition),
  })
}

/**
 * A non-support check is keyed by position *and* check type, since several
 * checks (bending, tension, …) can share a position.
 */
export const isSelectedCheckHighlightMatch = (
  elementGuid: string,
  highlightedCheck: HighlightedCheck,
  relativePosition: string | number | undefined,
  checkType: MemberCheckType,
): boolean =>
  highlightedCheck != null &&
  highlightedCheck.kind === 'check' &&
  highlightedCheck.elementGuid === elementGuid &&
  highlightedCheck.checkType === checkType &&
  matchesRelativePosition(highlightedCheck.relativePosition, relativePosition)

export const toggleSelectedCheckHighlight = (
  elementGuid: string,
  relativePosition: string | number,
  checkType: MemberCheckType,
  highlightedCheck: HighlightedCheck,
  setHighlightedCheck: (value: HighlightedCheck) => void,
): void => {
  if (isSelectedCheckHighlightMatch(elementGuid, highlightedCheck, relativePosition, checkType)) {
    setHighlightedCheck(null)
    return
  }
  setHighlightedCheck({
    kind: 'check',
    elementGuid,
    relativePosition: String(relativePosition),
    checkType,
  })
}

export const isMemberCheckRowHighlighted = (
  elementGuid: string | undefined,
  highlightedCheck: HighlightedCheck,
  row: MemberCheckRow,
): boolean => {
  if (!elementGuid || !('relative_position' in row.check)) return false
  if (isSupportCompressionMemberCheckType(row.checkType)) {
    return isSelectedSupportHighlightMatch(
      elementGuid,
      highlightedCheck,
      row.check.relative_position,
    )
  }
  return isSelectedCheckHighlightMatch(
    elementGuid,
    highlightedCheck,
    row.check.relative_position,
    row.checkType,
  )
}

export const isTimberLikeSettings = (
  settings: SettingsOnMember | null | undefined,
): settings is TimberCheckSettings | TimberSlabCheckSettings =>
  settings?.setting_type === 'timber' || settings?.setting_type === 'timber-slab'

/**
 * Ordnet jeder Auflagerzeile (support_config.relative_position) genau einen
 * SupportCompression-/SteelSupportCompression-Check zu — nächster Nachbar innerhalb `tolerance`,
 * jeder Check höchstens einmal (wie die CSAndAP-Zuordnung per Distanz, aber eindeutig pro Zeile).
 */
export function assignNearestUniqueSupportCompressionChecks<
  T extends SupportCompressionStructuralCheck | SteelCompressionCheck,
>(
  checks: T[],
  supportRelativePositions: Array<string | number | undefined | null>,
  tolerance: number,
): (T | undefined)[] {
  const rowCount = supportRelativePositions.length
  const result: (T | undefined)[] = Array.from({ length: rowCount }, () => undefined)
  if (!checks.length || rowCount === 0) return result

  const usedCheckIndices = new Set<number>()

  supportRelativePositions.forEach((relPos, rowIdx) => {
    if (relPos === undefined || relPos === null || relPos === '') return
    const target = round(toNumber(relPos as string | number), 3)
    if (!Number.isFinite(target)) return

    let bestIdx = -1
    let bestDist = Infinity
    checks.forEach((c, ci) => {
      if (usedCheckIndices.has(ci)) return
      const d = Math.abs(round(toNumber(c.relative_position), 3) - round(target, 3))
      if (d < bestDist) {
        bestDist = d
        bestIdx = ci
      }
    })

    if (bestIdx >= 0 && bestDist <= tolerance) {
      result[rowIdx] = checks[bestIdx]
      usedCheckIndices.add(bestIdx)
    }
  })

  return result
}

export const isUnsetSupportNumber = (value: unknown): boolean =>
  value === null || value === undefined || value === ''

export const coalesceSupportNumber = (value: unknown, fallback: number): number => {
  if (isUnsetSupportNumber(value)) return fallback
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : fallback
}

/**
 *
 * @deprecated
 * @param settings
 * @param supportConfigsForChecks
 * @returns
 */
export const mergeSupportConfigDefaultsIntoRequest = (
  settings: SettingsOnMember,
  supportConfigsForChecks: SupportConfigForChecks[],
): SettingsOnMember => {
  if (!isTimberLikeSettings(settings) || !settings.support_configs?.length) return settings
  if (!supportConfigsForChecks.length) return settings

  const nextSupportConfigs = settings.support_configs.map(config => {
    const defaults = supportConfigsForChecks.find(({ relative_position }) =>
      matchesRelativePosition(relative_position, config.relative_position),
    )
    if (!defaults) return config

    const nextArea =
      config.area == null
        ? defaults.area
        : defaults.area == null
        ? config.area
        : {
            length: coalesceSupportNumber(config.area.length, defaults.area.length),
            width: coalesceSupportNumber(config.area.width, defaults.area.width),
          }

    const nextOverhang =
      config.overhang == null
        ? defaults.overhang
        : defaults.overhang == null
        ? config.overhang
        : {
            left: coalesceSupportNumber(config.overhang.left, defaults.overhang.left),
            right: coalesceSupportNumber(config.overhang.right, defaults.overhang.right),
            front: coalesceSupportNumber(config.overhang.front, defaults.overhang.front),
            back: coalesceSupportNumber(config.overhang.back, defaults.overhang.back),
          }

    return {
      ...config,
      area: nextArea,
      overhang: nextOverhang,
    }
  })

  return {
    ...settings,
    support_configs: nextSupportConfigs,
  }
}

export const mergeIncomingSettingsPreservingEmptySupportGeometry = (
  current: SettingsOnMember,
  incoming: SettingsOnMember,
): SettingsOnMember => {
  if (!isTimberLikeSettings(current) || !isTimberLikeSettings(incoming)) return incoming
  // Important behavior for this form:
  // We *do not* want backend-calculated support geometry defaults to overwrite the form values.
  // Instead we surface those defaults via placeholders and only materialize them into the request
  // payload on submit.
  return { ...incoming, support_configs: current.support_configs }
}

export const stripDocumentExtension = (fileName: string): string =>
  fileName.replace(/\.(pdf|docx?)$/i, '')

export const findElementCrossSection = (
  elementCrossSectionAssignment: ElementCSAssignment[] | null | undefined,
  elementId?: string,
): CrossSection | undefined => {
  if (!elementId || !elementCrossSectionAssignment?.length) return undefined
  return find(elementCrossSectionAssignment, { element_guid: elementId })?.element_cs
}

/** Min/max reaction of one support with the loaded fields behind each, if the load case is positioned. */
export const rangeOfSupport = (row: CharacteristicSupportReactionRow, supportIdx: number) => {
  const forceMin = row.forces_min_newton?.[supportIdx]
  const forceMax = row.forces_max_newton?.[supportIdx]
  const minLoadedFields = row.forces_min_loaded_fields?.[supportIdx]
  const maxLoadedFields = row.forces_max_loaded_fields?.[supportIdx]
  const positionedFields = row.positioned_fields
  if (
    forceMin === undefined ||
    forceMax === undefined ||
    !minLoadedFields ||
    !maxLoadedFields ||
    !positionedFields
  ) {
    return undefined
  }
  return { forceMin, forceMax, minLoadedFields, maxLoadedFields, positionedFields }
}
